import { useState } from 'react';
import { Loader2, Mail, Phone, Copy, Check, RefreshCw, Building2 } from 'lucide-react';
import { useGetEmployerReferencesQuery, useRetriggerEmployerReferenceMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { EmployerReference, EmployerReferenceRating } from '@/types';

interface Props {
    investigationId: string;
    // Only Super Admins may re-send; CS sees the list read-only.
    canRetrigger: boolean;
}

const RATING_STYLE: Record<EmployerReferenceRating, string> = {
    excellent: 'bg-green-50 text-green-700 border-green-100',
    good: 'bg-blue-50 text-blue-700 border-blue-100',
    bad: 'bg-red-50 text-red-700 border-red-100',
};

function RatingRow({ label, value }: { label: string; value: EmployerReferenceRating }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-[#4e4d4d]">{label}</span>
            <span
                className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium capitalize ${RATING_STYLE[value]}`}
            >
                {value}
            </span>
        </div>
    );
}

function ReferenceCard({ reference, canRetrigger }: { reference: EmployerReference; canRetrigger: boolean }) {
    const [retrigger, { isLoading }] = useRetriggerEmployerReferenceMutation();
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');

    const responded = reference.status === 'responded';
    const resp = reference.response;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(reference.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setCopied(false);
        }
    };

    const handleRetrigger = async () => {
        setError('');
        try {
            await retrigger({ referenceId: reference.id, investigationId: reference.investigationId }).unwrap();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not re-send this reference request.'));
        }
    };

    return (
        <div className="rounded-xl border border-[#e0e0e0] bg-white p-4">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 shrink-0 text-[#9a9a9a]" />
                        <p className="truncate text-sm font-semibold text-[#1b1b1b]">{reference.employerName}</p>
                    </div>
                    {reference.declaredPosition && (
                        <p className="mt-0.5 text-xs text-[#717680]">Declared role: {reference.declaredPosition}</p>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#717680]">
                        {reference.email && (
                            <span className="inline-flex items-center gap-1">
                                <Mail className="h-3 w-3" /> {reference.email}
                            </span>
                        )}
                        {reference.phone && (
                            <span className="inline-flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {reference.phone}
                            </span>
                        )}
                    </div>
                </div>
                <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                        responded ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                    }`}
                >
                    {responded ? 'Responded' : 'Pending'}
                </span>
            </div>

            {/* Shareable link + retrigger */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-[#e0e0e0] bg-[#fafafa] px-2.5 py-1.5">
                    <span
                        className="min-w-0 flex-1 truncate font-mono text-[11px] text-[#4e4d4d]"
                        title={reference.link}
                    >
                        {reference.link}
                    </span>
                    <button
                        type="button"
                        onClick={copyLink}
                        title="Copy link"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-[#717680] hover:bg-gray-200 hover:text-[#1b1b1b]"
                    >
                        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                </div>
                {canRetrigger && !responded && (
                    <button
                        type="button"
                        onClick={handleRetrigger}
                        disabled={isLoading}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#e0e0e0] bg-white px-3 py-1.5 text-xs font-medium text-[#1b1b1b] hover:bg-gray-50 disabled:opacity-60"
                    >
                        {isLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        Re-send
                    </button>
                )}
            </div>

            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

            {/* Submitted response */}
            {responded && resp && (
                <div className="mt-3 flex flex-col gap-2 rounded-lg border border-[#f0f0f0] bg-[#fafafa] p-3">
                    {(resp.positionHeld || resp.periodOfEmployment) && (
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#4e4d4d]">
                            {resp.positionHeld && (
                                <span>
                                    <span className="text-[#9a9a9a]">Position:</span> {resp.positionHeld}
                                </span>
                            )}
                            {resp.periodOfEmployment && (
                                <span>
                                    <span className="text-[#9a9a9a]">Period:</span> {resp.periodOfEmployment}
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex flex-col gap-1.5">
                        <RatingRow label="Work performance" value={resp.workPerformance} />
                        <RatingRow label="Professional conduct" value={resp.professionalConduct} />
                        <RatingRow label="Ability to adapt" value={resp.abilityToAdapt} />
                        <RatingRow label="Ability to learn and improve" value={resp.abilityToLearnAndImprove} />
                    </div>
                    {resp.disciplinaryActions && (
                        <p className="whitespace-pre-wrap text-xs text-[#4e4d4d]">
                            <span className="text-[#9a9a9a]">Disciplinary actions:</span> {resp.disciplinaryActions}
                        </p>
                    )}
                    {resp.reasonForLeaving && (
                        <p className="text-xs text-[#4e4d4d]">
                            <span className="text-[#9a9a9a]">Reason for leaving:</span> {resp.reasonForLeaving}
                        </p>
                    )}
                    {resp.generalRemarks && (
                        <p className="whitespace-pre-wrap text-xs text-[#4e4d4d]">
                            <span className="text-[#9a9a9a]">Remarks:</span> {resp.generalRemarks}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

export default function EmployerReferencesPanel({ investigationId, canRetrigger }: Props) {
    const { data: references = [], isLoading } = useGetEmployerReferencesQuery(investigationId, {
        skip: !investigationId,
    });

    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-[#FF6B35]" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <div>
                <h3 className="text-sm font-bold text-[#1b1b1b]">Previous employer references</h3>
                <p className="mt-0.5 text-xs text-[#4e4d4d]">
                    Reference requests are sent automatically to each previous employer with contact details. Employers
                    with no email or phone are not listed here.
                </p>
            </div>

            {references.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#e0e0e0] bg-[#fafafa] py-8 text-center">
                    <Building2 className="mx-auto mb-2 h-5 w-5 text-[#9a9a9a]" />
                    <p className="text-sm text-[#4e4d4d]">No employer reference requests for this investigation.</p>
                </div>
            ) : (
                references.map((ref) => <ReferenceCard key={ref.id} reference={ref} canRetrigger={canRetrigger} />)
            )}
        </div>
    );
}
